import { z } from 'zod';

import { EntityId, Page, PageQuery } from '../primitives';
import { SoftDelete, TextFilter, Timestamps } from './common';

/**
 * `artists.kind` (migration 0004, docs/02 §1): `placeholder` marks the pseudo-artists the
 * client's Excel uses instead of a name ("Standard", "Traditional", "Children's Songs"); the
 * apps render those songs as having **no** artist (docs/07 §4).
 */
export const ArtistKind = z.enum(['artist', 'placeholder']);
export type ArtistKind = z.infer<typeof ArtistKind>;

/** MusicBrainz artist MBID (migration 0003). */
export const Mbid = z
  .string()
  .trim()
  .toLowerCase()
  .regex(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/,
    'Expected a MusicBrainz UUID',
  );
export type Mbid = z.infer<typeof Mbid>;

export const CmsArtistDTO = z
  .object({
    id: EntityId,
    name: z.string(),
    nameNorm: z.string(),
    slug: z.string(),
    kind: ArtistKind,
    /** MusicBrainz artist id resolved by tools/catalog-identify (read/write, docs/07 §4). */
    mbid: z.string().nullable(),
    /** R2 key under `catalog-images/artists/…`. */
    imagePath: z.string().nullable(),
    /**
     * Display URL, always one of ours: `picture_key` (our R2 copy, WP0.9) or `image_path`. Never
     * `artists.image_url` — the clients must not hotlink Deezer's CDN, and a row with neither key
     * renders the placeholder (QA3 CMS-64).
     */
    imageUrl: z.string().nullable(),
    /** `artists.image_url` verbatim (Deezer), kept apart from the resolved `imageUrl`. */
    sourceImageUrl: z.string().nullable(),
    relevance: z.number().int(),
    songCount: z.number().int().nonnegative(),
  })
  .merge(Timestamps)
  .merge(SoftDelete);
export type CmsArtistDTO = z.infer<typeof CmsArtistDTO>;

/** `sort` values `GET /cms/artists` accepts. */
export const ArtistSorts = ['name', 'relevance', 'song_count', 'created_at', 'updated_at'] as const;
export const ArtistSort = z.enum(ArtistSorts);
export type ArtistSort = z.infer<typeof ArtistSort>;

/** `GET /cms/artists?q=&kind=&mbid=` (server-side filtering, docs/06 §4). */
export const CmsArtistsQuery = PageQuery.extend({
  q: TextFilter.optional(),
  kind: ArtistKind.optional(),
  /** `yes` → only artists with an MBID, `no` → only artists without one. */
  mbid: z.enum(['yes', 'no']).optional(),
});
export type CmsArtistsQuery = z.infer<typeof CmsArtistsQuery>;
export type CmsArtistsQueryInput = z.input<typeof CmsArtistsQuery>;

export const CmsArtistsResponse = Page(CmsArtistDTO);
export type CmsArtistsResponse = z.infer<typeof CmsArtistsResponse>;

/** `POST /cms/artists` */
export const CmsArtistCreateBody = z.object({
  name: z.string().trim().min(1).max(120),
  /** Derived from `name` when omitted. */
  slug: z
    .string()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/)
    .max(140)
    .optional(),
  kind: ArtistKind.optional(),
  mbid: Mbid.nullable().optional(),
  imagePath: z.string().max(512).nullable().optional(),
  relevance: z.number().int().min(0).max(1_000_000).optional(),
});
export type CmsArtistCreateBody = z.infer<typeof CmsArtistCreateBody>;
export type CmsArtistCreateBodyInput = z.input<typeof CmsArtistCreateBody>;

/** `PATCH /cms/artists/:id` — at least one field. */
export const CmsArtistPatchBody = CmsArtistCreateBody.partial()
  .strict()
  .refine((patch) => Object.keys(patch).length > 0, { message: 'Nothing to update' });
export type CmsArtistPatchBody = z.infer<typeof CmsArtistPatchBody>;
export type CmsArtistPatchBodyInput = z.input<typeof CmsArtistPatchBody>;

export const CmsArtistResponse = z.object({ artist: CmsArtistDTO });
export type CmsArtistResponse = z.infer<typeof CmsArtistResponse>;
